/**
 * Knowledge Freshness (section 20d) — pure, client-side heuristics that classify
 * a team document's freshness so stale knowledge is DISCOVERABLE. This module
 * NEVER mutates a document: it only computes a verdict the UI surfaces as a
 * badge. Marking a doc reviewed/deprecated/superseded stays a human action.
 *
 * States (see {@link FreshnessState}) and their precedence, highest first:
 *   1. SUPERSEDED   — a newer document replaces it (supersededById set). Most
 *                     actionable: go read the replacement.
 *   2. DEPRECATED   — the document itself is marked DEPRECATED or ARCHIVED.
 *   3. POSSIBLY_STALE — references deprecated infra (via tags), OR older than
 *                     the stale threshold since last review/creation.
 *   4. NEEDS_REVIEW — older than the review threshold.
 *   5. CURRENT      — recently reviewed/created and no other signal.
 *
 * Pure TS — no React.
 */

import { stalenessDays } from '../documents/documentModel';
import type { TeamDocumentSummary } from '../documents/types';
import type { BadgeTone } from '../../ui';
import type { FreshnessAssessment, FreshnessState } from './types';

/** Age (days since last review/creation) beyond which a doc needs a re-read. */
export const NEEDS_REVIEW_THRESHOLD_DAYS = 90;
/** Age beyond which a doc is flagged possibly stale. */
export const POSSIBLY_STALE_THRESHOLD_DAYS = 180;

/**
 * Tag substrings that signal a document references deprecated / retired infra.
 *
 * EXTENSION POINT: this is a small, transparent allowlist of signal words.
 * Swap it for a richer source (e.g. a live registry of deprecated systems, or a
 * cross-reference against Service records marked retired) without changing the
 * {@link assessFreshness} contract.
 */
export const DEPRECATED_INFRA_TAGS = [
  'deprecated',
  'legacy',
  'retired',
  'sunset',
  'eol',
  'end-of-life',
  'obsolete',
];

/** True when any of the document's tags references deprecated infra. */
export function referencesDeprecatedInfra(
  doc: Pick<TeamDocumentSummary, 'tags'>,
): boolean {
  const tags = (doc.tags ?? []).map((t) => t.toLowerCase());
  return tags.some((t) => DEPRECATED_INFRA_TAGS.some((d) => t.includes(d)));
}

/** The fields freshness depends on (a subset of the document summary). */
export type FreshnessInput = Pick<
  TeamDocumentSummary,
  'status' | 'supersededById' | 'lastReviewedDate' | 'createdDate' | 'tags'
>;

/**
 * Computes the freshness verdict for a document. Non-mutating; the caller
 * decides how (and whether) to surface it.
 */
export function assessFreshness(doc: FreshnessInput): FreshnessAssessment {
  const ageDays = stalenessDays(doc);

  if (doc.supersededById) {
    return {
      state: 'SUPERSEDED',
      reason: 'A newer document supersedes this one.',
      ageDays,
    };
  }
  if (doc.status === 'DEPRECATED') {
    return { state: 'DEPRECATED', reason: 'Marked deprecated.', ageDays };
  }
  if (doc.status === 'ARCHIVED') {
    return { state: 'DEPRECATED', reason: 'Archived / retired from active use.', ageDays };
  }
  if (referencesDeprecatedInfra(doc)) {
    return {
      state: 'POSSIBLY_STALE',
      reason: 'Tags reference deprecated / retired infrastructure.',
      ageDays,
    };
  }
  if (ageDays !== undefined && ageDays > POSSIBLY_STALE_THRESHOLD_DAYS) {
    return {
      state: 'POSSIBLY_STALE',
      reason: `No review in ${ageDays} days.`,
      ageDays,
    };
  }
  if (ageDays !== undefined && ageDays > NEEDS_REVIEW_THRESHOLD_DAYS) {
    return {
      state: 'NEEDS_REVIEW',
      reason: `Last reviewed ${ageDays} days ago.`,
      ageDays,
    };
  }
  return {
    state: 'CURRENT',
    reason:
      ageDays !== undefined
        ? `Reviewed ${ageDays} day${ageDays === 1 ? '' : 's'} ago.`
        : 'Recently registered.',
    ageDays,
  };
}

/** Human label for a freshness state. */
export function freshnessLabel(state: FreshnessState): string {
  switch (state) {
    case 'CURRENT':
      return 'Current';
    case 'NEEDS_REVIEW':
      return 'Needs review';
    case 'POSSIBLY_STALE':
      return 'Possibly stale';
    case 'SUPERSEDED':
      return 'Superseded';
    case 'DEPRECATED':
      return 'Deprecated';
  }
}

/** Badge tone for a freshness state. */
export function freshnessTone(state: FreshnessState): BadgeTone {
  switch (state) {
    case 'CURRENT':
      return 'success';
    case 'NEEDS_REVIEW':
      return 'info';
    case 'POSSIBLY_STALE':
      return 'warning';
    case 'SUPERSEDED':
      return 'warning';
    case 'DEPRECATED':
      return 'neutral';
  }
}

/** True when a state warrants attention (everything except CURRENT). */
export function freshnessNeedsAttention(state: FreshnessState): boolean {
  return state !== 'CURRENT';
}
